import { useCallback, useEffect, useMemo, useRef, useState, type Key } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Select, SelectItem, Spinner } from '@nextui-org/react';
import { CtyButton } from '@circuitry-ai/doc-ui';
import { useAdminClient, type ActivityEvent, type AdminAgent, type AdminUser, type Decision } from './api';
import { ErrorLine, TabIntro, useAdminLoad } from './shared';

export const ACTIVITY_PAGE_SIZE = 50;
const ANY = 'any';

/** The operations the data layer records (its tools), as an admin reads them. */
const OPERATIONS: Record<string, string> = {
  query_sql: 'SQL query',
  query_metric: 'Metric query',
  list_datasets: 'List datasets',
  describe_dataset: 'Describe dataset',
  explain: 'Explain',
  latest_record: 'Latest record',
};

const DECISIONS: Record<Decision, { label: string; color: 'success' | 'danger' | 'warning' }> = {
  allow: { label: 'Allowed', color: 'success' },
  deny: { label: 'Denied', color: 'danger' },
  error: { label: 'Error', color: 'warning' },
};

function localTime(at: string): string {
  const date = new Date(at);
  return Number.isNaN(date.getTime()) ? at : date.toLocaleString();
}

/**
 * Activity: the data layer's audit log for the tenant, newest first, filtered by decision and
 * operation, with older pages on demand.
 */
export function ActivityTab({ baseUrl }: { readonly baseUrl: string }) {
  const { t } = useTranslation();
  const client = useAdminClient(baseUrl);
  const [decision, setDecision] = useState<string>(ANY);
  const [operation, setOperation] = useState<string>(ANY);
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef<AbortController | null>(null);

  // Names for user and agent ids; best effort, the raw ids show without them.
  const { data: names } = useAdminLoad<{ users: AdminUser[]; agents: AdminAgent[] }>(client, async (c, signal) => {
    const [users, agents] = await Promise.allSettled([c.users(signal), c.agents(signal)]);
    return {
      users: users.status === 'fulfilled' ? users.value : [],
      agents: agents.status === 'fulfilled' ? agents.value : [],
    };
  });
  const userNames = useMemo(() => new Map((names?.users ?? []).map((u) => [u.user_id, u.name || u.email || u.user_id])), [names]);
  const agentNames = useMemo(() => new Map((names?.agents ?? []).map((a) => [a.id, a.label])), [names]);

  /** The newest page (`before` undefined) or the page older than `before`. */
  const fetchPage = useCallback(async (before?: string) => {
    if (!client) return;
    requestRef.current?.abort();
    const request = new AbortController();
    requestRef.current = request;
    setLoading(true);
    setError(null);
    try {
      const page = await client.activity({
        limit: ACTIVITY_PAGE_SIZE,
        before,
        decision: decision === ANY ? undefined : (decision as Decision),
        operation: operation === ANY ? undefined : operation,
      }, request.signal);
      if (request.signal.aborted) return;
      const fresh = Array.isArray(page?.events) ? page.events : [];
      setEvents((prev) => (before ? [...prev, ...fresh] : fresh));
      setNextBefore(page?.next_before ?? null);
      setLoaded(true);
    } catch (e) {
      if (!request.signal.aborted) setError((e as Error).message);
    } finally {
      if (!request.signal.aborted) setLoading(false);
    }
  }, [client, decision, operation]);

  // The newest page when the tab first has a client, and again when a filter changes.
  const ready = Boolean(client);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  useEffect(() => {
    if (ready) void fetchRef.current();
  }, [ready, decision, operation]);
  useEffect(() => () => requestRef.current?.abort(), []);

  const pick = (set: (value: string) => void) => (keys: 'all' | Set<Key>) => {
    if (keys === 'all') return;
    set(String(Array.from(keys)[0] ?? ANY));
  };

  return (
    <div className="flex flex-col gap-4 px-6 pb-6">
      <TabIntro
        title={t('Activity')}
        actions={(
          <CtyButton variant="flat" size="sm" onPress={() => void fetchPage()} isDisabled={loading || !client}>
            {t('Refresh')}
          </CtyButton>
        )}
      >
        <span className="text-sm text-default-500">
          {t('What AI agents asked the data layer for, for whom, and whether it was allowed. Newest first.')}
        </span>
        <span className="text-xs text-default-400">
          {t('Events are stored from when this feature was deployed; older activity is only in Datadog.')}
        </span>
      </TabIntro>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          label={t('Decision')}
          size="sm"
          className="max-w-[200px]"
          selectedKeys={[decision]}
          onSelectionChange={pick(setDecision)}
          disallowEmptySelection
        >
          {[
            <SelectItem key={ANY} value={ANY} textValue={t('Any decision')}>{t('Any decision')}</SelectItem>,
            ...Object.entries(DECISIONS).map(([key, value]) => (
              <SelectItem key={key} value={key} textValue={t(value.label)}>{t(value.label)}</SelectItem>
            )),
          ]}
        </Select>
        <Select
          label={t('Operation')}
          size="sm"
          className="max-w-[220px]"
          selectedKeys={[operation]}
          onSelectionChange={pick(setOperation)}
          disallowEmptySelection
        >
          {[
            <SelectItem key={ANY} value={ANY} textValue={t('Any operation')}>{t('Any operation')}</SelectItem>,
            ...Object.entries(OPERATIONS).map(([key, label]) => (
              <SelectItem key={key} value={key} textValue={t(label)}>{t(label)}</SelectItem>
            )),
          ]}
        </Select>
      </div>

      <ErrorLine message={error} />
      {loaded && events.length === 0 && !loading && <p className="text-sm text-default-500">{t('No activity matches these filters.')}</p>}
      {events.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" aria-label={t('Activity')}>
            <thead className="text-xs text-default-500">
              <tr>
                <th className="py-1 pr-4 font-medium">{t('Time')}</th>
                <th className="py-1 pr-4 font-medium">{t('User')}</th>
                <th className="py-1 pr-4 font-medium">{t('Agent')}</th>
                <th className="py-1 pr-4 font-medium">{t('Operation')}</th>
                <th className="py-1 pr-4 font-medium">{t('Decision')}</th>
                <th className="py-1 pr-4 font-medium">{t('Datasets')}</th>
                <th className="py-1 pr-4 font-medium">{t('Rows')}</th>
                <th className="py-1 font-medium">{t('Reason')}</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event, index) => {
                const shown = DECISIONS[event.decision] ?? { label: event.decision, color: 'default' as const };
                const agent = event.agent_id ? agentNames.get(event.agent_id) ?? event.agent_id : '—';
                return (
                  <tr key={`${event.query_id}-${event.at}-${index}`} className="border-t border-default-100 align-top">
                    <td className="whitespace-nowrap py-1 pr-4 text-xs" title={event.at}>{localTime(event.at)}</td>
                    <td className="py-1 pr-4 text-xs" title={event.user_sub}>{userNames.get(event.user_sub) ?? event.user_sub}</td>
                    <td className="py-1 pr-4 text-xs" title={event.agent_id ?? undefined}>
                      {agent}
                      {event.agent_kind ? <span className="text-default-500"> ({event.agent_kind})</span> : null}
                    </td>
                    <td className="py-1 pr-4 text-xs">{t(OPERATIONS[event.operation] ?? event.operation)}</td>
                    <td className="py-1 pr-4">
                      <Chip size="sm" variant="flat" color={shown.color}>{t(shown.label)}</Chip>
                    </td>
                    <td className="py-1 pr-4 font-mono text-xs">{(event.datasets ?? []).join(', ') || '—'}</td>
                    <td className="py-1 pr-4 text-xs">{event.row_count ?? '—'}</td>
                    <td className="py-1 font-mono text-xs text-default-500">{event.reason_code ?? ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {loading && <Spinner size="sm" className="self-start" />}
      {nextBefore && events.length > 0 && (
        <CtyButton variant="flat" size="sm" className="self-start" onPress={() => void fetchPage(nextBefore)} isDisabled={loading}>
          {t('Load older')}
        </CtyButton>
      )}
    </div>
  );
}

export default ActivityTab;
